import type { PlayerPublic } from '@ringmaster/shared';
import { Skull } from 'lucide-react';
import { motion } from 'framer-motion';

/**
 * PlayerList — the living/dead roster. Optionally selectable (for target picks
 * and votes) with highlighted ids. FINAL signature; A6 restyles.
 */
export interface PlayerListProps {
  players: PlayerPublic[];
  highlightIds?: string[];
  onSelect?: (playerId: string) => void;
  selectable?: boolean;
}

export function PlayerList({ players, highlightIds, onSelect, selectable }: PlayerListProps) {
  const highlight = new Set(highlightIds ?? []);

  return (
    <ul
      style={{
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'grid',
        gap: 'var(--space-2)',
      }}
    >
      {players.map((p, i) => {
        const isHighlighted = highlight.has(p.id);
        const clickable = selectable && p.alive;
        const selected = isHighlighted && selectable;

        return (
          <motion.li
            key={p.id}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.04, duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.button
              type="button"
              className={clickable ? 'rm-hover-row' : undefined}
              disabled={!clickable}
              onClick={clickable ? () => onSelect?.(p.id) : undefined}
              whileTap={clickable ? { scale: 0.98 } : undefined}
              style={{
                width: '100%',
                minHeight: 44,
                textAlign: 'left',
                padding: '10px 14px',
                borderRadius: 'var(--radius-md)',
                border: selected
                  ? '1px solid var(--text-primary)'
                  : `1px solid ${isHighlighted ? 'var(--accent-blood-end)' : 'var(--surface-stroke)'}`,
                background: selected
                  ? 'rgba(255,255,255,0.08)'
                  : isHighlighted && !selectable
                  ? 'rgba(128,38,54,0.12)'
                  : 'transparent',
                color: p.alive ? 'var(--text-primary)' : 'var(--text-tertiary)',
                cursor: clickable ? 'pointer' : 'default',
                fontFamily: 'var(--font-ui)',
                fontSize: 'var(--text-md)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--space-2)',
                transition: 'border-color var(--duration-fast) var(--ease-standard), background var(--duration-fast) var(--ease-standard), filter var(--duration-fast) var(--ease-standard)',
                opacity: p.alive ? 1 : 0.4,
              }}
            >
              {/* Name with dead strikethrough */}
              <span
                style={{
                  textDecoration: p.alive ? 'none' : 'line-through',
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {p.name}
              </span>

              {/* Right-side badge */}
              <span
                style={{
                  color: 'var(--text-secondary)',
                  fontSize: 'var(--text-sm)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  flexShrink: 0,
                }}
              >
                {p.alive ? (
                  p.connected ? null : (
                    <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)' }}>
                      offline
                    </span>
                  )
                ) : (
                  <>
                    <Skull size={13} aria-hidden />
                    {p.revealedRole ? (
                      <span style={{ fontSize: 'var(--text-xs)', opacity: 0.8 }}>
                        {p.revealedRole}
                      </span>
                    ) : null}
                  </>
                )}
              </span>
            </motion.button>
          </motion.li>
        );
      })}
    </ul>
  );
}
